"use client";

import Image from "next/image";
import { motion, useMotionValue, useScroll, useSpring, useTransform, type MotionValue } from "framer-motion";
import { useEffect, useRef, type PointerEvent } from "react";
import type { Lang } from "@/lib/lang";
import { COPY } from "./copy";
import { BluetoothIcon, BoxIcon, PanoramaIcon, PersonOffIcon, usePrefersReducedMotion } from "./ui";

/*
 * One continuous shot, driven by scroll. It opens on the camera at work on a
 * floodlit pitch and pushes in until it lands on the object in the studio; the
 * camera then comes apart (closed, lid lifting, fully exploded — renders
 * generated as one chain, so they stay registered and crossfade as one object);
 * the lenses fill the screen and we dive through the centre one, and the iris
 * opens on what it sees: the whole pitch in one panorama — which is exactly
 * where the AI-director section below picks up. Labels are real text; every
 * claim is true of the camera we ship.
 */

type Window4 = [number, number, number, number];

// Six beats, as scroll-progress windows: [fade in from, fully in, still in, gone by].
const PHASES: Window4[] = [
  [-1, 0, 0.085, 0.12], // on the pitch
  [0.15, 0.185, 0.27, 0.305], // the object
  [0.31, 0.345, 0.42, 0.455], // inside
  [0.47, 0.51, 0.68, 0.715], // apart
  [0.725, 0.76, 0.83, 0.86], // the lenses
  [0.895, 0.93, 1.01, 1.02], // what it sees
];
const PART_START = 0.53;
const PART_STEP = 0.022;
const PARTS_OUT: [number, number] = [0.69, 0.72];

// Where things sit inside the photos (fractions of the image), so zooms land on them.
const PITCH_CAM = "75% 25.5%"; // the camera on its tripod in camera-night.webp
// How far it travels (percent of its layer) to land where the studio render sits.
const PITCH_TRAVEL = { x: -25, y: 21.5, yWide: 36 };
const LENS = { x: 62, y: 49 }; // the centre lens in teardown-lenses.webp (percent)
const LENS_ASPECT = 2600 / 1451;
// The iris opens from that lens's radius (≈5.5% of the photo's width) already zoomed ~1.8×.
const IRIS_FROM = 0.1;

// An ease-in that starts gently, so a zoom accelerates into its target.
const easeIn = (t: number) => t * t * t;
const easeOut = (t: number) => 1 - (1 - t) ** 3;
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const isWide = () => typeof window !== "undefined" && window.innerWidth >= 1024;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

function Caption({ q, win, title, body, over }: { q: MotionValue<number>; win: Window4; title: string; body: string; over?: string }) {
  const opacity = useTransform(q, win, [0, 1, 1, 0]);
  const y = useTransform(q, [win[0], win[1], win[2], win[3]], [26, 0, 0, -22]);
  return (
    <motion.div className="absolute inset-x-0 top-0" style={{ opacity, y }}>
      {over && <p className="sn-over" style={{ color: "var(--sn-gold)" }}>{over}</p>}
      <h2 className="sn-wide mt-3 max-w-[18ch] text-[clamp(28px,4.4vw,64px)]">{title}</h2>
      {body && <p className="mt-4 max-w-[30rem] text-[15px] leading-relaxed sm:text-[17px]" style={{ color: "rgba(255,255,255,.72)" }}>{body}</p>}
    </motion.div>
  );
}

/** One labelled part on the exploded view: dot, gold leader line, label. Hovering the label lights its part. */
function Part({ q, i, part, lang }: { q: MotionValue<number>; i: number; part: (typeof COPY.teardown.parts)[number]; lang: Lang }) {
  const a = PART_START + i * PART_STEP;
  const draw = useTransform(q, [a, a + 0.03, ...PARTS_OUT], [0, 1, 1, 0]);
  const fade = useTransform(q, [a + 0.012, a + 0.035, ...PARTS_OUT], [0, 1, 1, 0]);
  const dot = useTransform(q, [a - 0.005, a + 0.01, ...PARTS_OUT], [0, 1, 1, 0]);
  const left = part.side === "left";
  return (
    <div className="group pointer-events-none absolute inset-0">
      {/* Desktop: a solid gold leader line growing from the stage edge to the part. */}
      <motion.span
        className="absolute hidden h-px lg:block"
        style={{
          top: `${part.y * 100}%`,
          ...(left ? { left: 0, width: `${part.x * 100}%`, transformOrigin: "left" } : { right: 0, width: `${(1 - part.x) * 100}%`, transformOrigin: "right" }),
          scaleX: draw, opacity: dot,
          background: left
            ? "linear-gradient(90deg, rgba(226,189,114,.35), #E2BD72)"
            : "linear-gradient(270deg, rgba(226,189,114,.35), #E2BD72)",
        }}
        aria-hidden="true"
      />
      <motion.div
        className={`pointer-events-auto absolute hidden w-[240px] -translate-y-1/2 cursor-default lg:block ${left ? "pr-5 text-right" : "pl-5 text-left"}`}
        style={{ top: `${part.y * 100}%`, ...(left ? { right: "100%" } : { left: "100%" }), opacity: fade }}
      >
        <p className="text-[15px] font-semibold transition-colors duration-300 group-hover:text-[var(--sn-gold-soft)]">{part.title[lang]}</p>
        <p className="mt-1 text-[13px] leading-snug" style={{ color: "var(--sn-fg2)" }}>{part.body[lang]}</p>
      </motion.div>
      {/* The anchor: a gold dot with a soft ring (numbered on phones). */}
      <motion.span
        className="absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full lg:h-3 lg:w-3"
        style={{ left: `${part.x * 100}%`, top: `${part.y * 100}%`, scale: dot, background: "var(--sn-gold)", boxShadow: "0 0 0 6px rgba(226,189,114,.18), 0 0 24px rgba(226,189,114,.6)" }}
        aria-hidden="true"
      >
        <span className="absolute inset-[-10px] rounded-full border border-[rgba(226,189,114,.7)] opacity-0 transition duration-300 group-hover:scale-125 group-hover:opacity-100" />
        <span className="sn-num text-[11px] lg:hidden" style={{ color: "var(--sn-gold-ink)" }}>{i + 1}</span>
      </motion.span>
    </div>
  );
}

function PartRow({ q, i, part, lang }: { q: MotionValue<number>; i: number; part: (typeof COPY.teardown.parts)[number]; lang: Lang }) {
  const a = PART_START + i * PART_STEP;
  const opacity = useTransform(q, [a, a + 0.03, ...PARTS_OUT], [0.25, 1, 1, 0.25]);
  return (
    <motion.li className="flex items-baseline gap-3" style={{ opacity }}>
      <span className="sn-num w-4 shrink-0 text-[12px]" style={{ color: "var(--sn-gold)" }}>{i + 1}</span>
      <span className="text-[13.5px] leading-snug">
        <span className="font-semibold">{part.title[lang]}</span>
        <span style={{ color: "var(--sn-fg2)" }}> · {part.body[lang]}</span>
      </span>
    </motion.li>
  );
}

/** A chapter tick: fills as its beat arrives; jumps there when pressed. */
function Tick({ q, win, label, jump, onJump }: { q: MotionValue<number>; win: Window4; label: string; jump: string; onJump: () => void }) {
  const fill = useTransform(q, [win[0] < 0 ? 0 : win[0], win[1]], [0, 1]);
  const active = useTransform(q, win, [0.5, 1, 1, 0.5]);
  return (
    <button type="button" onClick={onJump} aria-label={`${jump}: ${label}`} className="group flex flex-col items-center gap-2 py-2">
      <span className="h-[3px] w-8 overflow-hidden rounded-full transition-[width] duration-300 group-hover:w-12 sm:w-10" style={{ background: "rgba(255,255,255,.18)" }}>
        <motion.span className="block h-full w-full origin-left rounded-full" style={{ scaleX: fill, background: "var(--sn-gold)" }} />
      </span>
      <motion.span className="sn-over hidden !text-[10px] !tracking-[0.16em] lg:block" style={{ opacity: active, color: "#fff" }}>{label}</motion.span>
    </button>
  );
}

/** Warm dust drifting up through the light — drawn only while it can be seen. */
function GoldDust({ opacity }: { opacity: MotionValue<number> }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    const cv = ref.current;
    const ctx = cv?.getContext("2d");
    if (!cv || !ctx || reduced) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    let w = 0, h = 0, raf = 0, onScreen = false;
    const motes = Array.from({ length: 64 }, () => ({
      x: Math.random(), y: Math.random(), r: 0.5 + Math.random() * 1.6,
      v: 0.00012 + Math.random() * 0.00042, ph: Math.random() * Math.PI * 2,
    }));
    const size = () => {
      w = cv.clientWidth; h = cv.clientHeight;
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const frame = (t: number) => {
      ctx.clearRect(0, 0, w, h);
      for (const m of motes) {
        m.y -= m.v;
        m.x += Math.sin(t / 2600 + m.ph) * 0.00011;
        if (m.y < -0.02) { m.y = 1.02; m.x = Math.random(); }
        // Brightest in the middle of the frame, where the light is.
        const centre = 1 - Math.min(1, Math.hypot(m.x - 0.5, (m.y - 0.55) * 1.3) * 1.5);
        const a = centre * (0.35 + 0.5 * (0.5 + 0.5 * Math.sin(t / 820 + m.ph)));
        if (a <= 0.01) continue;
        ctx.fillStyle = `rgba(240,213,158,${(a * 0.18).toFixed(3)})`;
        ctx.beginPath(); ctx.arc(m.x * w, m.y * h, m.r * 3.2, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `rgba(255,236,196,${a.toFixed(3)})`;
        ctx.beginPath(); ctx.arc(m.x * w, m.y * h, m.r, 0, Math.PI * 2); ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    };
    const sync = () => {
      const run = onScreen && opacity.get() > 0.02;
      if (run && !raf) raf = requestAnimationFrame(frame);
      if (!run && raf) { cancelAnimationFrame(raf); raf = 0; }
    };
    size();
    const ro = new ResizeObserver(size);
    ro.observe(cv);
    const io = new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; sync(); });
    io.observe(cv);
    const off = opacity.on("change", sync);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); off(); };
  }, [opacity, reduced]);
  return (
    <motion.canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-[6] h-full w-full"
      style={{ opacity, mixBlendMode: "screen" }}
    />
  );
}

export function CameraTeardown({ lang }: { lang: Lang }) {
  const t = COPY.teardown;
  const features = COPY.camera.features;
  const icons = [BluetoothIcon, PanoramaIcon, BoxIcon, PersonOffIcon];
  const ref = useRef<HTMLElement>(null);
  const reduced = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });
  const q = reduced ? scrollYProgress : smooth;

  // ── 1 · On the pitch, then the push-in to the camera. ──
  // The camera travels to the middle of the stage, where the studio render takes over, while
  // the shot pushes in. The zoom always leads the travel (≥ 1 + 1.5×), so the photo's edges
  // never come into view.
  const push = useTransform(q, [0.055, 0.17], [0, 1]);
  const travel = useTransform(push, easeInOut);
  const pitchScale = useTransform(push, (u) => 1 + 1.5 * easeInOut(u) + 0.9 * easeIn(u));
  const travelX = useTransform(travel, (v) => `${v * PITCH_TRAVEL.x}%`);
  const travelY = useTransform(travel, (v) => `${v * (isWide() ? PITCH_TRAVEL.yWide : PITCH_TRAVEL.y)}%`);
  const pitchO = useTransform(q, [0.115, 0.165], [1, 0]);
  const pitchDim = useTransform(q, [0.06, 0.15], [0, 0.75]);
  const featuresO = useTransform(q, [0.045, 0.085], [1, 0]);
  const featuresY = useTransform(q, [0.045, 0.085], [0, 24]);

  // ── 2–4 · The object on the studio stage. ──
  const stageO = useTransform(q, [0.12, 0.18, ...PARTS_OUT], [0, 1, 1, 0]);
  const stageScale = useTransform(q, [0.12, 0.2, 0.69, 0.74], [0.72, 1, 1, 1.05]);
  const f0 = useTransform(q, [0.3, 0.35], [1, 0]);
  const f1 = useTransform(q, [0.3, 0.35, 0.445, 0.5], [0, 1, 1, 0]);
  const f1Scale = useTransform(q, [0.445, 0.5], [1, 1.03]);
  const f2 = useTransform(q, [0.465, 0.52], [0, 1]);
  // The exploded frame arrives stretching upward from the base, so the layers read as rising.
  const f2ScaleY = useTransform(q, [0.465, 0.53], [0.9, 1]);
  const f2Scale = useTransform(q, [0.465, 0.53], [0.97, 1]);
  const sweep = useTransform(q, [0.175, 0.29], ["-45%", "125%"]);
  const glow = useTransform(q, [0.12, 0.2, 0.45, 0.55, 0.69, 0.74], [0, 0.4, 0.5, 1, 1, 0]);
  const glowScale = useTransform(q, [0.3, 0.55], [0.85, 1.15]);
  const dust = useTransform(q, [0.14, 0.22, 0.66, 0.72], [0, 0.85, 0.85, 0]);
  const listO = useTransform(q, [0.51, 0.55, ...PARTS_OUT], [0, 1, 1, 0]);

  // ── 5 · The lenses, then the dive through the centre one. ──
  const lensO = useTransform(q, [0.69, 0.75, 0.935, 0.95], [0, 1, 1, 0]);
  const lensScale = useTransform(q, (v) =>
    v < 0.84 ? 1.04 + 0.18 * clamp01((v - 0.72) / 0.12) : 1.22 + 3.2 * easeIn(clamp01((v - 0.84) / 0.09)),
  );
  const countO = useTransform(q, [0.745, 0.77, 0.83, 0.855], [0, 1, 1, 0]);
  const count = useTransform(q, (v) => easeOut(clamp01((v - 0.75) / 0.06)));
  const countW = useTransform(count, (c) => String(Math.round(7168 * c)));
  const countH = useTransform(count, (c) => String(Math.round(2160 * c)));
  const gap = useTransform(q, [0.775, 0.825], [10, 0]);

  // ── 6 · The iris opens on what it sees. Its radius starts at the lens's own size. ──
  const iris = useTransform(q, (v) => {
    const k = IRIS_FROM + 1.25 * easeIn(clamp01((v - 0.855) / 0.075));
    return `circle(calc(max(100vw, ${(100 * LENS_ASPECT).toFixed(1)}svh) * ${k.toFixed(4)}) at ${LENS.x}% ${LENS.y}%)`;
  });
  const ringSize = useTransform(q, (v) => {
    const k = IRIS_FROM + 1.25 * easeIn(clamp01((v - 0.855) / 0.075));
    return `calc(max(100vw, ${(100 * LENS_ASPECT).toFixed(1)}svh) * ${(2 * k).toFixed(4)})`;
  });
  const ringO = useTransform(q, [0.85, 0.865, 0.895, 0.925], [0, 1, 1, 0]);
  const panoScale = useTransform(q, [0.855, 0.95], [1.3, 1]);
  const panoPan = useTransform(q, [0.9, 1], ["24% 50%", "76% 50%"]);
  const panoO = useTransform(q, [0.85, 0.858], [0, 1]);
  const hudO = useTransform(q, [0.93, 0.96], [0, 1]);

  // Pointer parallax on desktop: the camera, its light and the dust sit at different depths.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 60, damping: 18 });
  const sy = useSpring(py, { stiffness: 60, damping: 18 });
  const camX = useTransform(sx, (v) => v * 12);
  const camY = useTransform(sy, (v) => v * 8);
  const lightX = useTransform(sx, (v) => v * -22);
  const lightY = useTransform(sy, (v) => v * -14);
  const onPointer = (e: PointerEvent<HTMLDivElement>) => {
    if (reduced || e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    px.set(((e.clientX - r.left) / r.width - 0.5) * 2);
    py.set(((e.clientY - r.top) / r.height - 0.5) * 2);
  };

  const jumpTo = (i: number) => {
    const el = ref.current;
    if (!el) return;
    const w = PHASES[i];
    const at = i === 0 ? 0 : (w[1] + Math.min(1, w[2])) / 2;
    const top = el.getBoundingClientRect().top + window.scrollY + at * (el.offsetHeight - window.innerHeight);
    window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
  };

  return (
    <section id="camera" ref={ref} className="relative h-[640vh] scroll-mt-0 lg:h-[760vh]" aria-label={t.over[lang]}>
      <div className="sticky top-0 h-[100svh] overflow-hidden" onPointerMove={onPointer}>

        {/* 1 · The camera at work. The zoom's origin is the camera itself. */}
        <motion.div className="absolute inset-x-0 bottom-0 top-[24%] lg:top-0" style={{ opacity: pitchO }}>
          <motion.div className="absolute inset-0" style={{ x: travelX, y: travelY, scale: pitchScale, transformOrigin: PITCH_CAM }}>
            <Image src="/landing/camera-night.webp" alt={COPY.camera.tag[lang]} fill sizes="100vw" className="object-cover" style={{ objectPosition: PITCH_CAM }} />
          </motion.div>
        </motion.div>
        {/* Shade over the whole frame (the zoomed photo grows past its own box). */}
        <motion.div className="pointer-events-none absolute inset-0" style={{ opacity: pitchO }}>
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #05070d 0%, rgba(5,7,13,.86) 22%, rgba(5,7,13,.2) 44%, rgba(5,7,13,.25) 62%, rgba(5,7,13,.92) 100%)" }} />
          <motion.div className="absolute inset-0" style={{ opacity: pitchDim, background: "#05070d" }} />
        </motion.div>

        {/* 5 · The lenses. Positioned so the centre lens sits at a known point we zoom into. */}
        <motion.div className="absolute inset-0" style={{ opacity: lensO }}>
          <motion.div className="absolute inset-0" style={{ scale: lensScale, transformOrigin: `${LENS.x}% ${LENS.y}%` }}>
            <Image src="/landing/teardown-lenses.webp" alt={t.alts.lenses[lang]} fill sizes="100vw" className="object-cover" style={{ objectPosition: `${LENS.x}% ${LENS.y}%` }} />
          </motion.div>
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,7,13,.95) 0%, rgba(5,7,13,.8) 26%, rgba(5,7,13,.3) 44%, rgba(5,7,13,.06) 56%, rgba(5,7,13,.1) 70%, rgba(5,7,13,.88) 100%)" }} />
        </motion.div>

        {/* 6 · Through the lens: the pitch as the camera sees it. */}
        <motion.div className="absolute inset-0" style={{ opacity: panoO, clipPath: iris, WebkitClipPath: iris }}>
          <motion.div className="absolute inset-0" style={{ scale: panoScale }}>
            <motion.img
              src="/landing/panorama.webp"
              alt={COPY.director.alt[lang]}
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: panoPan }}
              loading="lazy"
              draggable={false}
            />
          </motion.div>
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,7,13,.9) 0%, rgba(5,7,13,.5) 26%, rgba(5,7,13,0) 48%, rgba(5,7,13,0) 70%, #05070d 100%)" }} />
        </motion.div>
        <motion.span
          aria-hidden="true"
          className="pointer-events-none absolute aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ left: `${LENS.x}%`, top: `${LENS.y}%`, width: ringSize, opacity: ringO, boxShadow: "0 0 0 1.5px rgba(226,189,114,.9), 0 0 40px 4px rgba(226,189,114,.45), inset 0 0 40px rgba(226,189,114,.35)" }}
        />

        <GoldDust opacity={dust} />

        <div className="relative mx-auto flex h-full max-w-[1360px] flex-col px-5 pb-4 pt-20 sm:px-8 sm:pt-24">
          {/* Captions: one per beat, crossfading in place. */}
          <div className="relative z-10 min-h-[150px] sm:min-h-[190px]">
            {t.phases.map((ph, i) => (
              <Caption key={ph.title.en} q={q} win={PHASES[i]} title={ph.title[lang]} body={ph.body[lang]} over={t.over[lang]} />
            ))}
          </div>

          {/* 2–4 · The object: registered renders on one stage, labels on the exploded view. */}
          <div className="relative flex flex-1 items-center justify-center">
            <motion.div className="relative flex w-full items-center justify-center" style={{ x: camX, y: camY }}>
              <motion.div
                className="relative aspect-[4/3] w-full max-w-[calc((100svh-330px)*4/3)] lg:max-w-[min(calc((100svh-300px)*4/3),calc(100%-560px))]"
                style={{ scale: stageScale, opacity: stageO }}
              >
                {/* The renders sit on pure black; feather their edges into the page ground. */}
                <div className="absolute inset-0" style={{ WebkitMaskImage: "radial-gradient(ellipse 72% 72% at 50% 50%, #000 62%, transparent 100%)", maskImage: "radial-gradient(ellipse 72% 72% at 50% 50%, #000 62%, transparent 100%)" }}>
                  <motion.div className="absolute inset-0" style={{ opacity: f0 }}>
                    <Image src="/landing/teardown-t0.webp" alt={t.alts.closed[lang]} fill sizes="(min-width:1024px) 60vw, 100vw" className="object-contain" />
                  </motion.div>
                  {/* A band of light crossing the closed camera — masked by the render's own
                      brightness, so it only catches the rims and the lettering. */}
                  <motion.div
                    aria-hidden="true"
                    className="absolute inset-0 overflow-hidden"
                    style={{
                      opacity: f0, mixBlendMode: "screen",
                      maskImage: "url(/landing/teardown-t0.webp)", WebkitMaskImage: "url(/landing/teardown-t0.webp)",
                      maskMode: "luminance", maskSize: "contain", WebkitMaskSize: "contain",
                      maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat", maskPosition: "center", WebkitMaskPosition: "center",
                    }}
                  >
                    <motion.span className="absolute inset-y-0 w-[34%] -skew-x-12" style={{ left: sweep, background: "linear-gradient(90deg, rgba(255,236,196,0), rgba(255,236,196,.9), rgba(255,236,196,0))" }} />
                  </motion.div>
                  <motion.div className="absolute inset-0" style={{ opacity: f1, scale: f1Scale }}>
                    <Image src="/landing/teardown-t1.webp" alt={t.alts.open[lang]} fill sizes="(min-width:1024px) 60vw, 100vw" className="object-contain" />
                  </motion.div>
                  <motion.div className="absolute inset-0" style={{ opacity: f2, scale: f2Scale, scaleY: f2ScaleY, transformOrigin: "50% 88%" }}>
                    <Image src="/landing/teardown-t2.webp" alt={t.alts.exploded[lang]} fill sizes="(min-width:1024px) 60vw, 100vw" className="object-contain" />
                  </motion.div>
                </div>
                {/* Labels live outside the feathered layer so the gutters stay crisp. */}
                <motion.div className="absolute inset-0" style={{ opacity: f2 }}>
                  {t.parts.map((part, i) => (
                    <Part key={part.title.en} q={q} i={i} part={part} lang={lang} />
                  ))}
                </motion.div>
                {/* Soft floor so the reflection melts into the page. */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[18%]" style={{ background: "linear-gradient(180deg, rgba(5,7,13,0), #05070d)" }} />
              </motion.div>
            </motion.div>

            {/* Phones: the parts as a numbered list under the camera. */}
            <motion.ol className="absolute inset-x-0 bottom-0 flex flex-col gap-1.5 lg:hidden" style={{ opacity: listO }} aria-label={t.phases[3].title[lang]}>
              {t.parts.map((part, i) => (
                <PartRow key={part.title.en} q={q} i={i} part={part} lang={lang} />
              ))}
            </motion.ol>

            {/* 1 · What the camera does for you at the pitch. */}
            <motion.ul className="absolute inset-x-0 bottom-0 z-10 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 lg:gap-3" style={{ opacity: featuresO, y: featuresY }}>
              {features.map((f, i) => {
                const Icon = icons[i];
                return (
                  <li key={f.en} className="sn-glass flex items-start gap-3 rounded-[18px] p-3.5 sm:p-4">
                    <Icon size={17} className="mt-0.5 shrink-0" />
                    <span className="text-[13.5px] leading-snug" style={{ color: "rgba(255,255,255,.88)" }}>{f[lang]}</span>
                  </li>
                );
              })}
            </motion.ul>

            {/* 5 · The panorama's size, counted up on the scoreboard; three slices closing into one. */}
            <motion.div className="pointer-events-none absolute bottom-0 left-0 z-10" style={{ opacity: countO }}>
              <p className="sn-num flex items-baseline gap-3 text-[clamp(40px,6.4vw,92px)] leading-none">
                <motion.span>{countW}</motion.span>
                <span className="text-[0.5em]" style={{ color: "var(--sn-gold)" }}>×</span>
                <motion.span>{countH}</motion.span>
              </p>
              <div className="mt-4 flex items-center gap-4">
                <motion.span className="flex h-[6px] w-[110px] shrink-0 sm:w-[190px]" style={{ gap }}>
                  {[0, 1, 2].map((s) => <span key={s} className="h-full flex-1 rounded-full" style={{ background: "var(--sn-gold)" }} />)}
                </motion.span>
                <span className="sn-over !text-white">{t.pixels[lang]} · {t.stitch[lang]}</span>
              </div>
            </motion.div>

            {/* 6 · The camera's own read-out over its view. */}
            <motion.div className="pointer-events-none absolute bottom-1 left-0 z-10 flex items-center gap-2 rounded-full px-3 py-1.5" style={{ opacity: hudO, background: "rgba(5,7,13,.6)", backdropFilter: "blur(10px)" }}>
              <span className="sn-rec h-2 w-2 rounded-full" style={{ background: "var(--sn-bad)" }} />
              <span className="sn-num text-[12px]">{COPY.director.panorama[lang]}</span>
            </motion.div>
          </div>

          {/* Gold light over the camera, screen-blended so it lifts the render's black
              exactly as much as the page around it — no box. Strongest at the AI board. */}
          <motion.div
            className="pointer-events-none absolute left-1/2 top-[56%] z-[5] h-[90vmin] w-[90vmin] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ opacity: glow, scale: glowScale, x: lightX, y: lightY, mixBlendMode: "screen", background: "radial-gradient(closest-side, rgba(226,189,114,.22), rgba(226,189,114,.05) 55%, rgba(226,189,114,0))" }}
          />

          <nav className="relative z-10 mt-3 flex justify-center gap-2 sm:gap-3" aria-label={t.over[lang]}>
            {PHASES.map((w, i) => (
              <Tick key={i} q={q} win={w} label={t.chapters[i][lang]} jump={t.jump[lang]} onJump={() => jumpTo(i)} />
            ))}
          </nav>
        </div>
      </div>
    </section>
  );
}
